import { useEffect, useMemo, useState } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import FixedFloat from 'src/app/components/Elements/Page/Standard/FixedFloat';
import { SearchBar } from 'src/app/components/Elements/SearchBar';
import Spinner from 'src/app/components/Elements/Spinner';
import { StyledLink } from 'src/app/components/Elements/StyledLink';
import { ViewOrderToast } from 'src/app/components/Elements/ToastBanner/presets/ViewOrder';
import { OTCChatCard } from 'src/app/components/Models/OTC/OTCChatCard';
import { checkoutAddonSearch } from 'src/app/components/Pages/Essentials/CheckoutAddons/checkoutAddonSearch';
import { getCheckoutPath } from 'src/app/components/Pages/Essentials/CheckoutAddons/utils';
import { useFacilityByAccountId } from 'src/app/hooks/data/facility';
import { useOTCMedCategoriesV2 } from 'src/app/hooks/data/otc';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import * as paths from 'src/app/router/paths';
import { getCartItemQuantity } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import type { OTCCategoryV2, OTCItemV2 } from 'src/app/types';

import { EssentialsItemCard } from '../EssentialsItemCard/EssentialsItemCard';
import { essentialsMoneyFormatter } from '../utils';

const Page = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.capsuleBlue50};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 32px;

  ${({ theme }) => theme.mediaQueries.medium} {
    background-color: ${({ theme }) => theme.colors.capsuleGreen00};
    margin: 0;
  }
  ${({ theme }) => theme.mediaQueries.mediumDown} {
    padding: ${({ theme }) => theme.space.s6};
  }
`;

const SearchResultsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 230px));
  grid-gap: 5px;
  ${({ theme }) => theme.mediaQueries.smallDown} {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }
`;

const BackLink = styled(Link)`
  & > span {
    color: ${({ theme }) => theme.colors.capsuleGreen60};
    margin-left: ${({ theme }) => theme.space.s2};
  }
`;

const StyledSearchBar = styled(SearchBar)`
  position: relative;
  width: 100%;
`;

const BackArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none">
    <path
      d="M4.97575 8.15767L2.13386 5L4.97575 1.84233C5.17475 1.62123 5.17475 1.26766 4.97575 1.04655C4.76989 0.817815 4.43011 0.817815 4.22425 1.04655L1.02425 4.60211C0.825251 4.82321 0.825251 5.17679 1.02425 5.39789L4.22425 8.95345C4.43011 9.18219 4.76989 9.18219 4.97575 8.95345C5.17475 8.73234 5.17475 8.37877 4.97575 8.15767Z"
      fill="#008674"
      stroke="#008674"
      stroke-width="0.25"
    />
  </svg>
);

export const EssentialsCategory = () => {
  const trackSearchEvent = useSegmentEvent('search', { shouldTrackOnMount: false });
  const trackAddToCartEvent = useSegmentEvent('addToCart', { shouldTrackOnMount: false });
  const trackRemoveFromCartEvent = useSegmentEvent('removeFromCart', { shouldTrackOnMount: false });
  const trackBeginCheckoutEvent = useSegmentEvent('beginCheckout', { shouldTrackOnMount: false });

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const category = decodeURIComponent(searchParams.get('category') || '');

  useSegmentEvent('viewEssentialsCategory', { shouldTrackOnMount: true, payload: { aisle: category } });

  const [isUpsellEssentialsEnabled, setUpsellEssentialsEnabled] = useState(false);

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));

  const { fetchVariant } = useExperiments();
  const { data: facility, isSuccess: hasFacilityLoaded } = useFacilityByAccountId(user ? parseInt(user.accountId) : 0);

  useEffect(() => {
    if (!!user && hasFacilityLoaded) {
      const fetchUpsellEssentialsExperiment = async () => {
        const variant = await fetchVariant({
          experimentKey: 'upsell_essentials_12_21_23',
          userId: user.accountId,
          audienceAttributes: {
            facility_code: facility?.code,
          },
        });
        setUpsellEssentialsEnabled(variant === 'on');
      };
      fetchUpsellEssentialsExperiment();
    }
  }, [user, fetchVariant, facility, hasFacilityLoaded]);

  const { data: allOtcCategories, isLoading } = useOTCMedCategoriesV2();

  const cartItemQuantity = useSelector(getCartItemQuantity);

  const [isSearching, setIsSearching] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState<OTCCategoryV2[]>([]);

  const categoryItems = useMemo(() => {
    const itemsWithDuplicate = isSearching
      ? filteredCategories.map(category => category.items).flat()
      : allOtcCategories
          ?.filter((c: OTCCategoryV2) => c.name === category)
          .map((c: OTCCategoryV2) => c.items)
          .flat();

    return itemsWithDuplicate?.reduce<OTCItemV2[]>((acc, obj) => {
      if (!acc.some(item => item.id === obj.id)) {
        acc.push(obj);
      }
      return acc;
    }, []);
  }, [allOtcCategories, category, filteredCategories, isSearching]);

  return (
    <Page>
      {isLoading || !allOtcCategories ? (
        <Spinner centered />
      ) : (
        <section
          css={css`
            position: relative;
            padding-bottom: ${({ theme }) => theme.space.s8};
            width: 100%;
          `}
        >
          <div
            css={css`
              margin-bottom: 24px;
            `}
          >
            <StyledSearchBar
              handleChange={e => {
                const searchText = e.currentTarget.value;
                setIsSearching(!!searchText);
                checkoutAddonSearch({ searchText, allOtcCategories, filteredCategories, setFilteredCategories });
              }}
              handleBlur={e => {
                const search_term = e.currentTarget.value;
                trackSearchEvent({ search_term, page: 'essentials' });
              }}
            />
          </div>
          <div
            css={css`
              margin-top: 32px;
            `}
          >
            <BackLink to={paths.shop2.toPath()}>
              <BackArrow />
              <Typography as="span">Back</Typography>
            </BackLink>
          </div>

          {!isSearching && (
            <Typography
              variant="heading3"
              css={css`
                margin: 20px 0;
              `}
            >
              {category}
            </Typography>
          )}

          {isSearching && categoryItems?.length === 0 && <OTCChatCard />}

          <SearchResultsContainer>
            {categoryItems?.map((item: OTCItemV2, itemIndex: number) => {
              const formattedPrice = essentialsMoneyFormatter.format(item.price);
              const analyticsCartManipulationPayload = {
                display_name_primary: item.name,
                dosage: item.dosage,
                price: formattedPrice,
                quantity: item.quantity ?? 1,
                OTC: true,
                patientmedication_id: null,
                prescription_fulfillment_id: null,
                page: 'essentials_product_listing',
              };

              const position = [1, itemIndex + 1];

              return (
                <EssentialsItemCard
                  {...item}
                  key={itemIndex}
                  formattedPrice={formattedPrice}
                  onAddToCart={() => trackAddToCartEvent({ ...analyticsCartManipulationPayload, position })}
                  onRemoveFromCart={() => trackRemoveFromCartEvent(analyticsCartManipulationPayload)}
                />
              );
            })}
          </SearchResultsContainer>

          {cartItemQuantity > 0 && (
            <FixedFloat
              css={css`
                z-index: 2;
              `}
            >
              <StyledLink
                variant={'noTextDecoration'}
                aria-label="View Order"
                onClick={() => trackBeginCheckoutEvent()}
                to={getCheckoutPath({ isUpsellEssentialsEnabled: isUpsellEssentialsEnabled })}
              >
                <ViewOrderToast cartItemCount={cartItemQuantity} />
              </StyledLink>
            </FixedFloat>
          )}
        </section>
      )}
    </Page>
  );
};
